import type { CompanySettingsAccess } from "~/auth/roleChecks";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { isExternalPath } from "~/helpers/aiChat/navigationDestinations";
import canOpenTenantSettings from "~/helpers/canOpenTenantSettings";

export interface DestinationAccess {
	canOrderRestock: boolean;
	canViewOwnCommissions: boolean;
	companySettings: CompanySettingsAccess;
	hasParentCompany: boolean;
	isFlagEnabled: (flag: FeatureFlagName) => boolean;
	isSuperAdmin: boolean;
}

const FLAGGED_PATHS: Array<[string, FeatureFlagName]> = [
	["/availability", FeatureFlagName.AvailabilityApp],
	["/mobile/availability", FeatureFlagName.AvailabilityApp],
	["/count", FeatureFlagName.CountApp],
	["/engage", FeatureFlagName.Engage],
	["/mobile/engage", FeatureFlagName.Engage],
	["/events", FeatureFlagName.EventsApp],
	["/forecast", FeatureFlagName.Forecast],
	["/fulfill/dashboard", FeatureFlagName.FulfillDashboard],
	["/fulfill/logistics", FeatureFlagName.FulfillLogistics],
	["/fulfill/product/kits", FeatureFlagName.FulfillKits],
	["/intelligence", FeatureFlagName.Intelligence],
	["/material", FeatureFlagName.MaterialApp],
	["/mobile/material", FeatureFlagName.MaterialApp],
	["/my-proposal", FeatureFlagName.PersonalizedPricingPage],
	["/notifications", FeatureFlagName.Notifications],
	["/portal", FeatureFlagName.CustomerPortal],
	["/produce", FeatureFlagName.Produce],
	["/produce/data-transform", FeatureFlagName.DataTransform],
	["/produce/production/plan-builder", FeatureFlagName.ProducePlanBuilder],
	["/restock", FeatureFlagName.Restock],
	["/verify", FeatureFlagName.Verify]
];

const SUPER_ADMIN_PATHS = ["/intelligence", "/my-proposal"];

const isWithin = (pathname: string, prefix: string): boolean =>
	pathname === prefix || pathname.startsWith(`${prefix}/`);

const OPEN_SETTINGS_PATHS = [
	"/settings/error-log",
	"/settings/profile",
	"/settings/preferences",
	"/settings/shortcuts",
	"/settings/notifications",
	"/settings/app-installs"
];

const canOpenSettings = (pathname: string, access: CompanySettingsAccess): boolean => {
	if (OPEN_SETTINGS_PATHS.includes(pathname)) return true;
	if (pathname === "/settings") return access.canEnter;
	if (pathname === "/settings/billing") return access.canViewBilling;
	return access.canManageUsers;
};

const canOpenDestination = (path: string, access: DestinationAccess): boolean => {
	if (isExternalPath(path)) return true;
	const pathname = path.trim().split(/[?#]/)[0];
	if (isWithin(pathname, "/settings/parent-company") && !access.hasParentCompany) return false;
	if (isWithin(pathname, "/settings")) return canOpenSettings(pathname, access.companySettings);
	if (!canOpenTenantSettings(pathname, access.companySettings.canManageUsers)) return false;
	if (pathname === "/my-commissions") return access.canViewOwnCommissions;
	if (isWithin(pathname, "/restock/orders") && !access.canOrderRestock) return false;
	if (!access.isSuperAdmin && SUPER_ADMIN_PATHS.some((prefix) => isWithin(pathname, prefix))) return false;
	return FLAGGED_PATHS.every(([prefix, flag]) => !isWithin(pathname, prefix) || access.isFlagEnabled(flag));
};

export default canOpenDestination;
